/**
 * Retention tab — the investor centerpiece.
 *
 * Layout:
 *   [ D1 / W1 / W4 / W12 / M6 / M12 checkpoint KPI cards ]
 *   [ Cohort heatmap (visx) — segment selector on header ]
 *   [ Retention curves line chart with industry benchmark overlay ]
 *   [ Conversion KPIs: Trial → Paid / Essentials → Chat+ / Monthly → Annual ]
 */
import React, { useMemo, useState } from 'react';
import { useAnalyticsQuery } from '../hooks/useAnalyticsQuery';
import { type AnalyticsRange } from '../hooks/useAnalyticsRange';
import type {
  CheckpointsPayload,
  CohortsPayload,
  CurvesPayload,
  SubscriptionSurvivalPayload,
} from '../types';
import { ChartCard } from '../components/ChartCard';
import { CohortHeatmap } from '../components/CohortHeatmap';
import { RetentionCurveChart } from '../components/RetentionCurveChart';
import { TrendChart } from '../components/TrendChart';
import { KpiTile } from '../components/KpiTile';
import { formatInt, formatPercent } from '../format';
import { cn } from '../../../../lib/cn';

const SEGMENTS: Array<{ key: string; label: string }> = [
  { key: 'all', label: 'All users' },
  { key: 'tier:basic', label: 'Essentials' },
  { key: 'tier:full', label: 'Chat+' },
  { key: 'tier:extended', label: 'Pro' },
];

export interface RetentionTabProps {
  range: AnalyticsRange;
}

export const RetentionTab: React.FC<RetentionTabProps> = ({ range }) => {
  const [segment, setSegment] = useState<string>('all');

  const cohorts = useAnalyticsQuery<CohortsPayload>(
    ['cohorts', segment, range.queryString],
    `/api/admin/analytics/retention/cohorts?${range.queryString}&segment=${encodeURIComponent(segment)}`,
  );
  const curves = useAnalyticsQuery<CurvesPayload>(
    ['curves', segment],
    `/api/admin/analytics/retention/curves?cohorts=8&segment=${encodeURIComponent(segment)}`,
  );
  const checkpoints = useAnalyticsQuery<CheckpointsPayload>(
    ['checkpoints', range.queryString],
    `/api/admin/analytics/retention/checkpoints?${range.queryString}`,
  );
  // Subscription survival is all-time (anchored on trial rollover), so no range key.
  const survival = useAnalyticsQuery<SubscriptionSurvivalPayload>(
    ['subscription-survival'],
    '/api/admin/analytics/retention/subscription-survival',
  );

  const chk = checkpoints.data?.data;
  const conv = chk?.conversions_30d;

  // Reshape the offset-arrays into TrendChart's row-per-x-tick format. Blended is
  // the primary line; up to 5 cohort lines (blended + cohorts ≤ 6 = theme cap).
  // null offsets pass through → honest gaps (TrendChart doesn't connectNulls).
  const sv = survival.data?.data;
  const survivalRows = useMemo(() => {
    if (!sv) return [];
    const rows: Array<Record<string, unknown>> = [];
    for (let m = 0; m <= sv.max_offset; m++) {
      const row: Record<string, unknown> = {
        month: m,
        blended: sv.blended_survival_pct_by_offset[m] ?? null,
      };
      sv.cohorts.slice(0, 5).forEach((c) => {
        row[c.cohort] = c.retained_pct_by_offset[m] ?? null;
      });
      rows.push(row);
    }
    return rows;
  }, [sv]);
  const survivalSeries = useMemo(() => {
    const base = [{ key: 'blended', label: 'Blended' }];
    const cohortSeries = (sv?.cohorts ?? []).slice(0, 5).map((c) => ({
      key: c.cohort,
      label: `${c.cohort} (n=${c.cohort_size})`,
    }));
    return [...base, ...cohortSeries];
  }, [sv]);

  return (
    <div className="space-y-6">
      {/* Checkpoint KPI cards */}
      <section>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          Retention checkpoints
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {checkpoints.isLoading &&
            Array.from({ length: 6 }).map((_, i) => (
              <KpiTile key={i} label="" value="" loading />
            ))}
          {chk?.checkpoints.map((cp) => (
            <KpiTile
              key={cp.label}
              label={cp.label}
              value={cp.value != null ? formatPercent(cp.value) : '—'}
              deltaPct={cp.delta_pct}
              hint={
                cp.cohort_week
                  ? `latest cohort ${new Date(cp.cohort_week + 'T00:00:00').toLocaleDateString(
                      'en-US',
                      { month: 'short', day: 'numeric' },
                    )}`
                  : undefined
              }
            />
          ))}
        </div>
      </section>

      {/* Subscription survival — the investor-grade retention metric.
          Cohort by trial rollover (paid subscription start), NOT signup. */}
      <ChartCard
        title="Subscription survival (by trial rollover)"
        subtitle={sv?.definition ?? 'Of users who started paying in month M0, the % still subscribed at M0+k. Anchored on trial rollover, not signup.'}
        loading={survival.isLoading}
        error={survival.error?.message ?? null}
        isEmpty={!sv || sv.total_subscribers === 0}
        onRetry={() => survival.refetch()}
      >
        <TrendChart
          data={survivalRows}
          xKey="month"
          type="line"
          height={300}
          series={survivalSeries}
          xFormat={(m) => `M${m}`}
          yFormat={(v) => `${Math.round(v)}%`}
        />
        <p className="mt-2 text-[11px] text-gray-500">
          {sv ? `${sv.total_subscribers} subscribers · x = months since paid subscription start · gaps = cohort not old enough to observe that month yet.` : ''}
        </p>
      </ChartCard>

      {/* Cohort heatmap */}
      <ChartCard
        title="Cohort retention heatmap"
        subtitle="Rows: signup week · Columns: weeks after signup · Cell: % retained. Tier segments group by FIRST PAID tier (stationary), not current tier."
        loading={cohorts.isLoading}
        error={cohorts.error?.message ?? null}
        isEmpty={!cohorts.data?.data.cohorts?.length}
        onRetry={() => cohorts.refetch()}
        headerAction={<SegmentPicker segment={segment} onChange={setSegment} />}
      >
        <CohortHeatmap cohorts={cohorts.data?.data.cohorts ?? []} />
      </ChartCard>

      {/* Retention curves — benchmark overlay removed (the old dashed line was
          an unsourced placeholder; re-add only with a citable source) */}
      <ChartCard
        title="Retention curves"
        subtitle="Top 8 most recent cohorts (most-recent-N — ignores the date range)"
        loading={curves.isLoading}
        error={curves.error?.message ?? null}
        isEmpty={!curves.data?.data.series?.length}
        onRetry={() => curves.refetch()}
      >
        <RetentionCurveChart
          series={curves.data?.data.series ?? []}
          benchmark={curves.data?.data.benchmark_curve}
        />
      </ChartCard>

      {/* Conversion tiles — heading reflects the ACTUAL selected range (was a
          hardcoded "Last 30 days" lie, audit F11) */}
      <section>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          Tier conversions — {range.label}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <KpiTile
            label="Trial → Paid"
            value={conv ? formatInt(conv.trial_to_paid.count) : '—'}
            hint={conv ? `${formatPercent(conv.trial_to_paid.pct)} of trial starts` : undefined}
            loading={checkpoints.isLoading}
          />
          <KpiTile
            label="Essentials → Chat+"
            value={conv ? formatInt(conv.essentials_to_chat_plus.count) : '—'}
            loading={checkpoints.isLoading}
          />
          <KpiTile
            label="Monthly → Annual"
            value={conv ? formatInt(conv.monthly_to_annual.count) : '—'}
            loading={checkpoints.isLoading}
          />
        </div>
      </section>
    </div>
  );
};

const SegmentPicker: React.FC<{ segment: string; onChange: (s: string) => void }> = ({
  segment,
  onChange,
}) => (
  <div className="inline-flex items-center gap-0.5 rounded-md border border-gray-200 bg-white p-0.5">
    {SEGMENTS.map((s) => {
      const active = segment === s.key;
      return (
        <button
          key={s.key}
          type="button"
          onClick={() => onChange(s.key)}
          className={cn(
            'rounded-md px-2 py-1 text-[11px] font-semibold transition-colors',
            active ? 'bg-coach-cedar text-white' : 'text-gray-600 hover:bg-gray-100',
          )}
        >
          {s.label}
        </button>
      );
    })}
  </div>
);
